import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  PersonalInfoApiError,
  PersonalInfoCreate,
} from "../../types/personal_info.types";
import { AddressCreate } from "../../types/address.types";
import {
  convertEmptyArrayToNull,
  convertEmptyToNull,
} from "../../utils/object-utils";
import { useToast } from "../../components/toast";
import { useNavigate } from "react-router-dom";
import { PersonalInfoForm } from "../../components/personal-info-form";
import { AddressForm } from "../../components/address-form";
import { Button } from "../../components/button";
import { ErrorMessage } from "../../components/error-message";
import { useErrorHandler } from "../../hooks/useErrorHandler";

export const ActivateAccountForm = () => {
  const { updateTokens } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    personalInfo: {
      dni: "",
      first_name: "",
      last_name: "",
      primary_phone_number: "",
      licence_number: "",
      birth_date: "",
      email: "",
      secondary_phone_number: "",
      emergency_contact_number: "",
      gender: undefined,
      blood_type: undefined,
    },
    addresses: [
      {
        address_line: "",
        city: "",
        state: "",
        country: "",
      },
    ],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { error, handleError, clearError } = useErrorHandler();
  const { activateAccount } = useAuth();
  const { addToast } = useToast();
  const [apiErrors, setApiErrors] = useState<PersonalInfoApiError>({
    dni: null,
    email: null,
    licence_number: null,
  });

  const handleChange = (e: React.ChangeEvent<HTMLElement>, index?: number) => {
    const { id, value } = e.target as HTMLInputElement | HTMLSelectElement;

    let formattedValue = value;

    if (id === "birth_date" && value) {
      const selectedDate = new Date(value);
      selectedDate.setUTCHours(0, 0, 0, 0);
      formattedValue = selectedDate.toISOString();
    }

    if (index !== undefined) {
      const updatedAddresses = [...formData.addresses];
      const fieldName = id.replace(/-\d+$/, "");
      updatedAddresses[index] = {
        ...updatedAddresses[index],
        [fieldName]: formattedValue,
      };
      setFormData((prev) => ({
        ...prev,
        addresses: updatedAddresses,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        personalInfo: {
          ...prev.personalInfo,
          [id]: formattedValue,
        },
      }));
    }

    if (error) clearError();
    setApiErrors({
      dni: null,
      email: null,
      licence_number: null,
    });
  };

  const handleActivation = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    clearError();

    const { personalInfo, addresses } = formData;

    if (
      !personalInfo.dni ||
      !personalInfo.first_name ||
      !personalInfo.last_name ||
      !personalInfo.primary_phone_number
    ) {
      handleError("Por favor, completa todos los campos obligatorios.");
      setIsSubmitting(false);
      return;
    }

    for (const address of addresses) {
      if (
        !address.address_line ||
        !address.city ||
        !address.state ||
        !address.country
      ) {
        handleError("Por favor, completa todos los campos de dirección.");
        setIsSubmitting(false);
        return;
      }
    }
    if (personalInfo.gender === undefined) {
      return;
    }
    const filteredpersonalInfo = {
      ...convertEmptyToNull(personalInfo),
    } as PersonalInfoCreate;
    const filteredAddresses = convertEmptyArrayToNull(
      addresses
    ) as AddressCreate[];

    try {
      const response = await activateAccount({
        personal_info: filteredpersonalInfo,
        addresses: filteredAddresses,
      });

      if (response.statusCode === 201) {
        addToast({
          message: "¡Cuenta activada!",
          type: "success",
          location: "bottom-center",
        });

        updateTokens(response.data.accessToken, response.data.refreshToken);

        setTimeout(() => {
          navigate("/dashboard", { replace: true });
        }, 50);

        return;
      } else {
        const errorCode = response.additionalData?.errorCode;
        if (errorCode === "DNI_IN_USE") {
          setApiErrors((prev) => ({
            ...prev,
            dni: "Este DNI ya está en uso.",
          }));
        } else if (errorCode === "EMAIL_IN_USE") {
          setApiErrors((prev) => ({
            ...prev,
            email: "Este correo electrónico ya está en uso.",
          }));
        } else if (errorCode === "LICENSE_NUMBER_IN_USE") {
          setApiErrors((prev) => ({
            ...prev,
            licence_number: "Este número de licencia ya está en uso.",
          }));
        }

        handleError(
          response.message ||
            "Hubo un problema con la activación de tu cuenta, intentalo de nuevo"
        );
      }
    } catch (error) {
      handleError(
        "Hubo un problema con la activación de tu cuenta, intentalo de nuevo"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleActivation} className="space-y-6">
      <PersonalInfoForm
        personalInfo={formData.personalInfo}
        onChange={handleChange}
        personalInfoApiError={apiErrors}
      />
      {formData.addresses.map((address, index) => (
        <div key={index} className="space-y-4">
          <AddressForm
            addresses={[address]}
            onChange={(e) => handleChange(e, index)}
          />
        </div>
      ))}

      {error && <ErrorMessage message={error} dismissible />}

      <Button
        type="submit"
        variant="auth"
        disabled={isSubmitting || error !== null}
        text="Activar Cuenta"
      />
    </form>
  );
};
